import { meetingAPI } from "@/api";
import { i18n } from "@/i18n";
import cloneDeep from "lodash/cloneDeep";
import orderBy from "lodash/orderBy";
import first from "lodash/first";
import Nprogress from "nprogress";
import VueNotifications from "vue-notifications";
import MeetingState from "@/constants/MeetingState";
import debugWrapper from "@/helpers/debugWrapper";

import {
	TeamingMeetingStartedEvent,
	TeamingMeetingJoinFailureEvent,
	TeamingMeetingJoinedEvent,
	TeamingMeetingExitedEvent,
	TeamingMeetingEndedEvent,
	TeamingMeetingFinalizedEvent,
	TopicOutcomeCreatedEvent,
	TopicOutcomeEditedEvent,
	PrivateNotesUpdatedEvent
} from "@/telemetry";

export default debugWrapper( "teaming:store:meeting:actions", {
	// NOTE: This does not appear to be used
	createOutcome: async ( { commit, rootGetters, /* state, rootState, */ }, { meetingId, topicId, newOutcome, } ) => {
		commit( "meetingStateUpdated", { meetingId, isCreatingTopicOutcome: true, } );
		Nprogress.start();
		try {
			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];
			const createdOutcome = await meetingAPI.createOutcome( { meetingId, topicId, newOutcome, } );

			commit( "schedule/outcomeCreated", {
				meetingId,
				createdOutcome,
				userProfile,
				timezone,
			}, { root: true, } );

			const telemetryEvent = new TopicOutcomeCreatedEvent( meetingId, newOutcome.type );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "meetingStore.createOutcome_errorMessage", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "meetingStateUpdated", { meetingId, isCreatingTopicOutcome: false, } );
	},
	fetchMeetingNote: async ( { commit, /* state, rootState, rootGetters, */ }, { meetingId, } ) => {
		commit( "meetingStateUpdated", { meetingId, isFetchingMeetingNote: true, } );
		Nprogress.start();
		try {
			const note = await meetingAPI.fetchMeetingNote( { meetingId, } );
			commit( "meetingNoteFetched", { meetingId, note, } );
		} catch ( e ) {
			const message = i18n.t( "meetingStore.fetchMeetingNote_errorMessage", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "meetingStateUpdated", { meetingId, isFetchingMeetingNote: false, } );
	},
	finalizeMeetingReport: async ( { commit, rootGetters, }, { meetingId, } ) => {
		commit( "meetingStateUpdated", { meetingId, isFinalizingMeeting: true, } );
		Nprogress.start();
		try {
			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];

			const updatedMeeting = await meetingAPI.finalizeMeetingReport( { meetingId, } );

			commit( "schedule/meetingUpdated", {
				meeting: updatedMeeting,
				userProfile,
				timezone,
			}, { root: true, } );

			const telemetryEvent = new TeamingMeetingFinalizedEvent( meetingId );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "meetingStore.queueMeetingReport_errorMessage", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "meetingStateUpdated", { meetingId, isFinalizingMeeting: false, } );
	},
	incrementStartedTopicDuration: ( { commit, }, { meetingId, seconds = 0, } ) => {
		commit( "topicDurationUpdated", { meetingId, seconds, } );
	},
	joinMeeting: async ( { commit, /* state, rootState, rootGetters, */ }, { meetingId, } ) => {
		commit( "meetingStateUpdated", { meetingId, isJoiningMeeting: true, } );
		Nprogress.start();
		try {
			await meetingAPI.joinMeeting( { meetingId, } );

			const telemetryEvent = new TeamingMeetingJoinedEvent( meetingId );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "meetingStore.joinMeeting_errorMessage", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );

			const telemetryEvent = new TeamingMeetingJoinFailureEvent( meetingId );
			telemetryEvent.submit();
		}
		Nprogress.done();
		// NOTE: see websocket handlers for where isJoiningMeeting is also set (to false)
	},
	leaveMeeting: async ( { getters, rootGetters, } ) => {
		// Would love a better way to get the meetingId here, but we're currently
		// limited by the fact that this is dispatched dynamically via menus.js config
		const meetingId = rootGetters[ "schedule/selectedScheduleMeetingId" ];

		if ( !meetingId ) {
			return;
		}

		const { isAttendingMeeting, } = getters;
		if ( !isAttendingMeeting( meetingId ) ) {
			return;
		}

		Nprogress.start();
		try {
			await meetingAPI.leaveMeeting( { meetingId, } );
			const telemetryEvent = new TeamingMeetingExitedEvent( meetingId );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "meetingStore.leaveMeeting_errorMessage", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
	},
	processAttendeeUpdate: async ( { commit, getters, }, { meetingId, attendees, } ) => {
		commit( "attendeesChanged", { meetingId, attendees, } );
		if ( getters.isAttendingMeeting( meetingId ) ) {
			commit( "meetingStateUpdated", { meetingId, isJoiningMeeting: false, } );
		}
	},
	processMeetingUpdate: async ( { commit, getters, rootGetters, }, { meeting, } ) => {
		const userProfile = rootGetters[ "userProfile/userProfile" ];
		const timezone = rootGetters[ "auth/timezone" ];

		const currentTopicIds = new Set( Object.keys( getters.topicStartTimeMap( meeting.id ) || {} ) );
		const newTopics = meeting.topics.filter( x => !currentTopicIds.has( x.id ) );

		if ( meeting.state === MeetingState.MEETING_STARTED ) {
			// setup
			commit( "meetingStarted", { meeting, userProfile, timezone, } );
			const selectedTopicId = getters.selectedTopicId( meeting.id );
			const { currentTopicId, } = meeting;
			commit( "meetingStateUpdated", {
				meetingId: meeting.id,
				selectedTopicId: selectedTopicId || currentTopicId || "",
				isStartingMeeting: false,
				isStartingTopic: false,
				isFetchingMeetingNote: false,
			} );
		}
		if ( meeting.state === MeetingState.MEETING_ENDED ) {
			// cleanup
			commit( "meetingStopped", { meeting, userProfile, timezone, } );
			commit( "meetingStateUpdated", { meetingId: meeting.id, isStoppingMeeting: false, } );
		}

		newTopics.forEach( topic => {
			commit( "meetingTopicCreated", {
				meetingId: meeting.id,
				createdTopic: topic,
			} );
		} );
	},
	processScheduleRefresh: async ( { commit, rootGetters, }, { meetings, } ) => {
		//
		// If a meeting is already in a started state, go ahead and start it for the current
		// user.
		//
		const startedMeetings = meetings.filter( meeting => meeting.state === MeetingState.MEETING_STARTED );
		const timezone = rootGetters[ "auth/timezone" ];
		if ( startedMeetings.length ) {
			for ( const startedMeeting of startedMeetings ) {
				const { id: meetingId, } = startedMeeting;
				commit( "meetingStarted", {
					timezone,
					meeting: cloneDeep( startedMeeting ),
				} );

				const fetchAttendeesPromise = meetingAPI.fetchAttendees( { meetingId, } );
				const fetchNotePromise = meetingAPI.fetchMeetingNote( { meetingId, } );

				// eslint-disable-next-line no-await-in-loop
				await Promise.all( [
					fetchAttendeesPromise,
					fetchNotePromise,
				] ).then( result => {
					const [ attendees, note, ] = result;
					commit( "attendeesChanged", {
						meetingId, attendees,
					} );
					commit( "meetingNoteFetched", {
						meetingId, note,
					} );
				} );
			}
		}
	},
	saveMeetingNote: async ( { commit, state, /* rootState, rootGetters, */ }, { meetingId, } ) => {
		Nprogress.start();
		try {
			const noteContent = state.meetingMap[ meetingId ].currentMeetingNote;
			await meetingAPI.saveMeetingNote( { meetingId, noteContent, isNew: state.isMeetingNoteNew, } );
			commit( "meetingNoteSaved", { meetingId, } );

			const telemetryEvent = new PrivateNotesUpdatedEvent( meetingId );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "meetingStore.saveMeetingNote_errorMessage", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
	},
	setMeetingNoteContent: async ( { commit, }, { meetingId, noteContent, } ) => {
		commit( "meetingNoteContentUpdated", { meetingId, noteContent, } );
	},
	startMeeting: async ( { commit, rootGetters, /* state, rootState, */ }, { meetingId, } ) => {
		Nprogress.start();
		try {
			const userProfile = rootGetters[ "userProfile/userProfile" ];
			commit( "meetingStateUpdated", {
				meetingId,
				isStartingMeeting: true,
				isFetchingMeetingNote: true,
				meetingInitiator: userProfile.personId,
			} );
			const startedMeeting = await meetingAPI.startMeeting( { meetingId, } );
			await meetingAPI.joinMeeting( { meetingId, } );
			const firstTopic = first( orderBy( startedMeeting.topics, [ "order", ], [ "asc", ] ) );
			if ( firstTopic ) {
				commit( "meetingStateUpdated", { meetingId, isStartingTopic: true, } );
				const { id: topicId, } = firstTopic;
				await meetingAPI.setCurrentTopic( { meetingId, topicId, } );
			}

			const isOrganizer = startedMeeting?.organizer?.personId === userProfile.personId;
			const telemetryEvent = new TeamingMeetingStartedEvent( meetingId, isOrganizer );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "meetingStore.startMeeting_errorMessage", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		// NOTE: meetingStarted mutation for meetingStartingId/meetingStartedId
	},
	startTopic: async ( { commit, dispatch, rootGetters, }, { topicId, meetingId, } ) => {
		if ( !meetingId ) {
			return;
		}
		const isDeferred = !!rootGetters[ "schedule/topicDeferralMap" ][ topicId ];
		commit( "meetingStateUpdated", { meetingId, isStartingTopic: true, } );
		Nprogress.start();
		try {
			if ( isDeferred ) {
				await dispatch( "schedule/undeferTopic", { meetingId, topicId, }, { root: true, } );
			}
			await meetingAPI.setCurrentTopic( { meetingId, topicId, } );
		} catch ( e ) {
			const message = i18n.t( "meetingStore.startTopic_errorMessage", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
	},
	stopMeeting: async ( { commit, state, getters, /* rootState, rootGetters, */ }, { meetingId, } ) => {
		commit( "meetingStateUpdated", { meetingId, isStoppingMeeting: true, } );
		Nprogress.start();
		try {
			const noteContent = state.meetingMap[ meetingId ].currentMeetingNote;
			const stopMeetingPromise = meetingAPI.stopMeeting( { meetingId, } );
			const saveNotePromise = getters.isMeetingNoteDirty( meetingId ) ?
				meetingAPI.saveMeetingNote( { meetingId, noteContent, isNew: state.isMeetingNoteNew, } ) :
				Promise.resolve();
			await Promise.all( [
				stopMeetingPromise,
				saveNotePromise,
			] );
			await meetingAPI.leaveMeeting( { meetingId, } );

			const telemetryEvent = new TeamingMeetingEndedEvent( meetingId );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "meetingStore.stopMeeting_errorMessage", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		// NOTE: see meetingStopped mutation for meetingStoppingId/meetingStoppedId
	},
	// NOTE: This does not appear to be used
	updateOutcome: async ( { commit, rootGetters, /* state, rootState, */ }, { meetingId, topicId, outcome, } ) => {
		commit( "meetingStateUpdated", { meetingId, isUpdatingTopicOutcome: true, } );
		Nprogress.start();
		try {
			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];
			const updatedOutcome = await meetingAPI.updateOutcome( { meetingId, topicId, outcome, } );

			commit( "schedule/outcomeUpdated", {
				meetingId,
				updatedOutcome,
				userProfile,
				timezone,
			}, { root: true, } );

			const telemetryEvent = new TopicOutcomeEditedEvent( meetingId, outcome.type );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "meetingStore.updateOutcome_errorMessage", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "meetingStateUpdated", { meetingId, isUpdatingTopicOutcome: false, } );
	},
	selectTopic: async ( { commit, /* state, rootState, rootGetters, */ }, { meetingId, topicId, } ) => {
		commit( "topicSelected", { meetingId, topicId, } );
	},
}, { skipFirstArg: true, } );



// WEBPACK FOOTER //
// ./src/store/modules/meeting/actions.js